import React, { useEffect, useRef, useState, useCallback } from "react";
import { Container, Row, Col, Button, Card } from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import {
  fetchallUsersByPractices,
  getUserPracticeById,
  getServicesByPracticeNumbers,
} from "../../api";
import { EditServices } from "./EditServices";
import { AddServiceInline } from "./AddServices";
import "./Services.css";

export function ViewServices({
  refresh,
  onServiceUpdate = () => {},
  addTrigger,
}) {
  const [allServices, setAllServices] = useState([]);
  const [practices, setPractices] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editingServiceId, setEditingServiceId] = useState(null); // <<— NEW: replaces expandedServiceId
  const addRef = useRef(null);
  const firstTriggerSeen = useRef(true);
  const lastTriggerRef = useRef(addTrigger);

const reload = useCallback(async () => {
  const token = sessionStorage.getItem("user");
  if (!token) return;

  const decoded = jwtDecode(token);
  const userPractices = await getUserPracticeById(decoded._id);
  setPractices(Array.isArray(userPractices) ? userPractices : []);

  const practiceNumbers = (Array.isArray(userPractices) ? userPractices : [])
    .map((p) => p.practiceNumber)
    .filter(Boolean);

  const services = await getServicesByPracticeNumbers(practiceNumbers);
  setAllServices(Array.isArray(services) ? services : []);

  const users = await fetchallUsersByPractices(practiceNumbers);
  setAllUsers(Array.isArray(users) ? users : []);
}, []);

useEffect(() => {
  reload();
}, [reload, refresh]);


  // Only open Add when header button is clicked (skip first render)
  useEffect(() => {
    if (firstTriggerSeen.current) {
      firstTriggerSeen.current = false;
      return;
    }
    if (lastTriggerRef.current !== addTrigger) {
      setShowAdd(true);
      setEditingServiceId(null); // ensure no editor shows while adding
      lastTriggerRef.current = addTrigger;

      setTimeout(() => {
        addRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
    }
  }, [addTrigger]);

  const handleEditClick = (serviceId) => {
    setShowAdd(false);
    setEditingServiceId(serviceId); // enter “cover” edit mode
  };

  const exitEditMode = () => setEditingServiceId(null);

  // child → parent refresh hooks
  const handleAdded = async () => {
    await reload();
    onServiceUpdate();
    setShowAdd(false);
  };

  const handleEdited = async () => {
    await reload();
    onServiceUpdate();
    setEditingServiceId(null);
  };

  return (
    <Container fluid className="bg-transparent">
      {/* Top inline Add panel; opened ONLY via header button */}
      <div ref={addRef} />
      {showAdd && (
        <Card className="mb-3 shadow-sm">
          <Card.Header
            className="d-flex justify-content-between align-items-center"
            style={{ background: "#CFE6FF" }}
          >
            <h6 className="mb-0 fw-bold text-primary">Add Service</h6>
            <Button
              variant="outline-secondary"
              size="sm"
              type="button"
              onClick={() => setShowAdd(false)}
            >
              Close
            </Button>
          </Card.Header>
          <Card.Body>
            <AddServiceInline
              onServiceUpdate={handleAdded}
              onClose={() => setShowAdd(false)}
            />
          </Card.Body>
        </Card>
      )}

      {(allServices ?? [])
  .slice() // don't mutate state
  .sort((a, b) => {
    const nameA = (a?.servicesName ?? "").toLowerCase();
    const nameB = (b?.servicesName ?? "").toLowerCase();
    return nameA.localeCompare(nameB);
  })
  .map((service) => {
    const isEditing = editingServiceId === service?._id;

    const tariffs = Array.isArray(service?.pricing?.selectedTariffs)
      ? service.pricing.selectedTariffs
      : [];

    const nappis = Array.isArray(service?.pricing?.selectedNappi)
      ? service.pricing.selectedNappi
      : Array.isArray(service?.pricing?.selectedNappis)
      ? service.pricing.selectedNappis
      : [];

    const svcPractices = Array.isArray(service?.servicesPractice)
      ? service.servicesPractice
      : [];

    const svcUsers = Array.isArray(service?.servicesUser)
      ? service.servicesUser
      : [];

    return (
      <Row key={service?._id} className="gy-3 mb-3">
        <Col md={12}>
          <Card className="shadow-sm">
            <Card.Header
              className="d-flex justify-content-between align-items-center"
              style={{ background: "#CFE6FF" }}
            >
              <div className="fw-bold text-primary">
                {service?.servicesName ?? "Untitled service"}
              </div>
              <div className="d-flex align-items-center gap-2">
                {isEditing ? (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    type="button"
                    onClick={exitEditMode}
                    aria-label="Close editor"
                    title="Close editor"
                  >
                    Close
                  </Button>
                ) : (
                  <Button
                    variant=""
                    type="button"
                    onClick={() => handleEditClick(service?._id)}
                    aria-label="Edit"
                    title="Edit"
                  >
                    <i className="fas fa-edit text-primary" />
                  </Button>
                )}
              </div>
            </Card.Header>

            <Card.Body className={isEditing ? "p-0" : undefined}>
              {isEditing ? (
                <div className="service-edit-cover">
                  <EditServices
                    inline
                    serviceId={service?._id}
                    onHide={exitEditMode}
                    onServiceUpdate={handleEdited}
                  />
                </div>
              ) : (
                <Row className="mb-3">
                  {/* Service Info */}
                  <Col md={4}>
                    <h6 className="text-primary">Service</h6>
                    <div className="text-muted">
                      {service?.servicesDescription ?? ""}
                    </div>
                    <div className="d-flex">
                      <strong style={{ minWidth: 130 }}>Duration:</strong>
                      <span>{service?.servicesDuration ?? ""}</span>
                    </div>
                    <div className="d-flex align-items-start">
                      <strong style={{ minWidth: 120 }}>Practice(s):</strong>
                      <div>
                        {svcPractices.length
                          ? svcPractices.map((pn, i) => (
                              <div key={i} className="ms-2">
                                {practices.find((p) => p.practiceNumber === pn)
                                  ?.practiceName || "Unknown Practice"}
                              </div>
                            ))
                          : <div className="ms-2 text-muted">None</div>}
                      </div>
                    </div>
                    <div className="d-flex align-items-start">
                      <strong style={{ minWidth: 120 }}>Assigned Users:</strong>
                      <div>
                        {svcUsers.length
                          ? svcUsers.map((uid, i) => {
                              const u = allUsers.find((x) => x.id === uid);
                              return (
                                <div key={i} className="ms-2">
                                  {u
                                    ? `${u.firstName} ${u.lastName}`
                                    : "Unknown User"}
                                </div>
                              );
                            })
                          : <div className="ms-2 text-muted">None</div>}
                      </div>
                    </div>
                  </Col>

                  {/* Billing */}
                  <Col md={4}>
                    <h6 className="text-primary">Billing</h6>
                    <div className="d-flex align-items-start">
                      <strong style={{ minWidth: 150 }}>Flat Rate:</strong>
                      <span>{service?.pricing?.flatRate ?? "Not Set"}</span>
                    </div>
                    <div className="d-flex align-items-start">
                      <strong style={{ minWidth: 150 }}>
                        Invoice Description:
                      </strong>
                      <span>{service?.pricing?.invoiceDescription ?? "Not Set"}</span>
                    </div>

                    <div><strong>Payment Options:</strong></div>
                    <div className="d-flex align-items-start ms-2">
                      <span style={{ minWidth: 150 }}>Flat Rate:</span>
                      <span className="ms-2">
                        {service?.pricing?.paymentOptions?.useFlatRate ? "Yes" : "No"}
                      </span>
                    </div>
                    <div className="d-flex align-items-start ms-2">
                      <span style={{ minWidth: 150 }}>Medical Aid Rate:</span>
                      <span className="ms-2">
                        {service?.pricing?.paymentOptions?.useFlatAndMedicalAidRate ? "Yes" : "No"}
                      </span>
                    </div>

                    <div className="mt-2">
                      <strong>Selected Tariff (Modifier) Codes:</strong>
                    </div>
                    {tariffs.length ? (
                      <div className="ms-2">
                        {tariffs.map((t, i) => {
                          const modCodes = Array.isArray(t?.modifiers)
                            ? t.modifiers.map((m) => m?.code).filter(Boolean)
                            : [];
                          const codeLabel = modCodes.length
                            ? `${t?.code} (${modCodes.join(", ")})`
                            : t?.code;

                          return (
                            <div
                              key={`tariff-${i}`}
                              className="m-0"
                              style={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                lineHeight: "1.4",
                                maxHeight: "2.8em",
                              }}
                              title={`${codeLabel ?? ""} — ${t?.description ?? ""}`}
                            >
                              <span>{codeLabel}</span> — {t?.description}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-muted ms-2">No tariffs selected</p>
                    )}

                    <div className="mt-2">
                      <strong>Selected NAPPI Codes:</strong>
                    </div>
                    {nappis.length ? (
                      <div className="ms-2">
                        {nappis.map((n, i) => (
                          <div
                            key={`nappi-${i}`}
                            className="m-0"
                            style={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              lineHeight: "1.4",
                              maxHeight: "2.8em",
                            }}
                            title={`${n?.nappiCode ?? ""} — ${n?.name ?? ""}`}
                          >
                            <span>{n?.nappiCode}</span> — {n?.name}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted ms-2">No NAPPIs selected</p>
                    )}
                  </Col>

                  {/* Settings */}
                  <Col md={4}>
                    <h6 className="text-primary">Settings</h6>
                    <div className="d-flex align-items-start">
                      <strong style={{ minWidth: 150 }}>Visible On Website:</strong>
                      <span>{service?.settings?.visible ? "Yes" : "No"}</span>
                    </div>
                    <div className="d-flex align-items-start">
                      <strong style={{ minWidth: 150 }}>Website Price:</strong>
                      <span>{service?.settings?.websitePrice ?? "Not Set"}</span>
                    </div>
                    <div className="d-flex align-items-start">
                      <strong style={{ minWidth: 150 }}>Bookable Online:</strong>
                      <span>{service?.settings?.bookable ? "Yes" : "No"}</span>
                    </div>
                    <div className="d-flex align-items-start">
                      <strong style={{ minWidth: 150 }}>Payable Upfront:</strong>
                      <span>{service?.settings?.upfrontPayable ? "Yes" : "No"}</span>
                    </div>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  })}

    </Container>
  );
}
